/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React from "react";
import {
	Typography,
	Tooltip,
	Checkbox,
	Card,
	IconButton,
	Dialog,
	DialogTitle,
	InputAdornment,
	CardContent,
	Grid,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { CONFIG_TOOL_TIPS } from "../../../../utility/toolTipMessages";
import FormTextField from "../../../../components/FormTextField/FormTextField";
import { TeamsSyncSetupDocumentation } from "./TeamsSyncSetupDocumentation";
import { updateConfigValues, getConfigValues } from "../../../../sharedServices/configService";
import { getUserStatistics } from "../../services/UserService";
import { v2, errorToObject } from "@prftesp/cle-logger";
import errorCodes from "../../../../utility/errorCodes";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { getDaysSince } from "../../../../utility/helpers";
import OfflineMapperTeamsStatuses from "./OfflineMapperTeamsStatuses";
import PACEToTeamsStatusMapper from "./PACEToTeamsStatusMapper";
import { Link } from "react-router-dom";
import { ActionButtons } from "../../../../components/Common";

const COMPONENT_NAME = "Admin SettingsManager/TeamsConfigSection";

/**
 * This component contains the fields for enabling or disabling Teams Status Syncing
 * @param {Number} connectedUsers The number of users actively syncing their Teams status.
 * @param {(settingName, settingValue) => void} setConfigValue A callback method for setting config values.
 * @param {String} teamSyncPipelineState The current state of the team sync state machine. Should be 'ENABLED' or 'DISABLED'.
 * @param {(Boolean) => void} setFormEdited A callback method for denoting with the form has been edited.
 * @param {Boolean} formEdited Boolean stating whether or not the form is edited
 */
export const TeamsConfigSection = ({ showSnackbar, setFormEdited, formEdited, setSaving, setPageLoading }) => {
	const [, setOriginalConfig] = React.useState(undefined);
	const [config, setConfig] = React.useState(undefined);
	const [statistics, setStatistics] = React.useState(undefined);
	const [editedConfigValuesObject, setEditedConfigValuesObject] = React.useState({});
	const [loading, setFormLoading] = React.useState(false);
	const [documentationOpen, setDocumentationOpen] = React.useState(false);
	const setLoading = React.useCallback(
		(newLoading) => {
			setPageLoading(newLoading);
			setFormLoading(newLoading);
		},
		[setPageLoading],
	);

	const setConfigValue = (configFieldName, value) => {
		const newConfig = { ...config };
		newConfig[configFieldName] = value;
		const newEditedConfig = { ...editedConfigValuesObject };
		newEditedConfig[configFieldName] = value;
		setConfig(newConfig);
		setEditedConfigValuesObject(newEditedConfig);
	};

	const setNewConfigValue = (settingName, settingValue) => {
		const newTeamsSyncConfig = { ...config.TeamsSyncConfiguration };
		newTeamsSyncConfig[settingName] = settingValue;
		setConfigValue("TeamsSyncConfiguration", newTeamsSyncConfig);
	};
	const { connectStatuses = [], connectToTeamsSyncEnabled = false } = config?.TeamsSyncConfiguration || {};
	const validationError = connectStatuses.length === 0 && connectToTeamsSyncEnabled;

	React.useEffect(() => {
		const fetchConfig = async () => {
			const configValueNames = ["TeamsSyncConfiguration", "TEAMS_USER_STATUS_SYNC_STATE"];
			try {
				const newConfig = await getConfigValues(configValueNames);
				setStatistics(await getUserStatistics());
				setConfig(newConfig);
				setOriginalConfig({ ...newConfig });
			} catch (error) {
				v2.error(`Error while fetching config values.`, {
					additionalData: { error: errorToObject(error), data: configValueNames },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_CONFIG_VALUES_ERROR,
				});
			} finally {
				setLoading(false);
			}
		};

		if (!config && !loading) {
			setLoading(true);
			fetchConfig();
		}
	}, [loading, setLoading, config]);

	const saveFunction = React.useCallback(async () => {
		setSaving(true);
		setEditedConfigValuesObject(async (edited) => {
			try {
				await updateConfigValues(edited);
				setOriginalConfig((original) => {
					setConfig({ ...original, ...edited });
					return { ...original, ...edited };
				});
				setFormEdited(false);
				showSnackbar("Successfully saved configuration.", "success");
				setSaving(false);
				return {};
			} catch (error) {
				v2.error(`Error while saving config values.`, {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.UPDATE_CONFIG_VALUES_ERROR,
				});
				showSnackbar("Failed to save configuration.", "error");
				setSaving(false);
			}
		});
	}, [setFormEdited, setSaving, showSnackbar]);

	const cancelFunction = React.useCallback(() => {
		setOriginalConfig((original) => {
			setConfig({ ...original });
			return original;
		});
		setEditedConfigValuesObject({});
		setFormEdited(false);
	}, [setFormEdited]);

	const isTeamsSyncPipelineEnabled = config?.TEAMS_USER_STATUS_SYNC_STATE === "ENABLED";
	const secretExpiryDate = config?.TeamsSyncConfiguration?.TeamsSyncClientSecretExpiryDate || null;
	const diffDays = getDaysSince(secretExpiryDate);

	const getClientSecretWarning = () => {
		if (config?.TeamsSyncConfiguration?.TeamsSyncClientSecret && secretExpiryDate && diffDays >= 0 && diffDays <= 14) {
			let warningMessage = "The Azure AD Client Secret will expire today. Please generate a new secret.";
			if (diffDays > 0) {
				warningMessage = `The Azure AD Client Secret will expire in ${diffDays} day${
					diffDays > 1 ? "s" : ""
				}. Please generate a new secret.`;
			}
			return (
				<span className="text-warning m-0">
					<i className="fas fa-exclamation-triangle"></i> {warningMessage}
				</span>
			);
		}
	};

	const getClientSecretExpiryError = (value) => {
		if (value && secretExpiryDate) {
			if (diffDays < 0) {
				return "The Azure AD Client Secret is expired. Please generate a new secret.";
			}
			return false;
		}
		return false;
	};

	return (
		<CardContent style={{ padding: "0 12px", width: "100%" }}>
			<Card style={{ margin: 0, padding: "20px", minHeight: "300px", width: "100%" }}>
				<Grid container alignItems="center" justifyContent="space-between" wrap="wrap">
					<div style={{ display: "flex" }}>
						<h5 style={{ fontWeight: "bold", margin: 0 }}>Microsoft Teams Connection Setup</h5>
						<IconButton onClick={() => setDocumentationOpen(true)} title="Open Documentation" size={"small"}>
							<i style={{ color: "gray" }} className="fas fa-info-circle" />
						</IconButton>
					</div>

					<div style={{ marginTop: "10px" }}>
						<ActionButtons
							className="my-2 my-md-0 justify-content-xs-start justify-content-md-end"
							showCancel={formEdited}
							showSave={formEdited}
							onSave={saveFunction}
							onCancel={cancelFunction}
							disableSave={validationError}
						/>
						{isTeamsSyncPipelineEnabled && config?.TeamsSyncConfiguration?.TeamsSyncEnabled && (
							<h6 className="mt-2">Users Currently Syncing: {statistics?.connectedWsUsers || 0}</h6>
						)}
					</div>
				</Grid>

				<Grid item className="mt-2">
					{!config && !loading && (
						<div className="mt-4">
							<span className="text-danger mr-2">Failed to retrieve config.</span>
						</div>
					)}
				</Grid>

				<Grid container justifyContent="space-between" spacing={2} className="mt-3">
					{config && (
						<>
							<Grid item xs={12} lg={6} className="mt-2">
								<div style={{ width: "100%" }}>
									<Typography style={{ fontSize: 12, color: "gray" }}>
										Microsoft Teams to PACE Status Sync
										{isTeamsSyncPipelineEnabled
											? ""
											: ` (Teams User Status Sync is currently not configured for this environment.)`}
									</Typography>
									<Tooltip
										arrow
										interactive
										placement={"bottom-start"}
										title={
											<React.Fragment>
												{CONFIG_TOOL_TIPS.ENABLE_TEAMS_STATUS_SYNC}
												{" Azure Active Directory IDs can be updated on the "}
												<Link style={{ textDecoration: "underline", color: "white" }} to="./userManagement">
													User Management page.
												</Link>
											</React.Fragment>
										}
									>
										<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
									</Tooltip>
									<Checkbox
										checked={(isTeamsSyncPipelineEnabled && config?.TeamsSyncConfiguration?.TeamsSyncEnabled) || false}
										disabled={!isTeamsSyncPipelineEnabled}
										onChange={(event) => {
											setFormEdited(true);
											setNewConfigValue("TeamsSyncEnabled", event.target.checked);
										}}
									/>
								</div>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<div style={{ width: "100%" }}>
									<OfflineMapperTeamsStatuses
										offlineStatuses={config?.TeamsSyncConfiguration?.offlineStatuses || []}
										onChange={setNewConfigValue}
										setFormEdited={setFormEdited}
									/>
								</div>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<div style={{ width: "100%" }}>
									<Typography style={{ fontSize: 12, color: "gray" }}>
										PACE to Microsoft Teams Status Sync
										{isTeamsSyncPipelineEnabled
											? ""
											: ` (Teams User Status Sync is currently not configured for this environment.)`}
									</Typography>
									<Tooltip
										arrow
										interactive
										placement={"bottom-start"}
										title={CONFIG_TOOL_TIPS.ENABLE_CONNECT_TO_TEAMS_STATUS_SYNC}
									>
										<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
									</Tooltip>
									<Checkbox
										checked={
											(isTeamsSyncPipelineEnabled && config?.TeamsSyncConfiguration?.connectToTeamsSyncEnabled) || false
										}
										disabled={!isTeamsSyncPipelineEnabled}
										onChange={(event) => {
											setFormEdited(true);
											setNewConfigValue("connectToTeamsSyncEnabled", event.target.checked);
										}}
									/>
								</div>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<div style={{ width: "100%" }}>
									<PACEToTeamsStatusMapper
										selectedStatuses={config?.TeamsSyncConfiguration?.connectStatuses || []}
										validationError={validationError}
										onChange={setNewConfigValue}
										setFormEdited={setFormEdited}
									/>
								</div>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<FormTextField
									label={"Azure AD Tenant ID"}
									placeholder="Tenant ID"
									formEdited={formEdited}
									styleObject={{ width: "100%" }}
									setEdited={setFormEdited}
									toolTipMessage={CONFIG_TOOL_TIPS.TEAMS_SYNC_TENANT_ID}
									startingValue={config?.TeamsSyncConfiguration?.TeamsSyncTenantId || ""}
									setConfigValue={setNewConfigValue}
									valueName={"TeamsSyncTenantId"}
								/>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<FormTextField
									label={"Azure AD Client ID"}
									placeholder="Client ID"
									formEdited={formEdited}
									styleObject={{ width: "100%" }}
									setEdited={setFormEdited}
									toolTipMessage={CONFIG_TOOL_TIPS.TEAMS_SYNC_CLIENT_ID}
									startingValue={config?.TeamsSyncConfiguration?.TeamsSyncClientId || ""}
									setConfigValue={setNewConfigValue}
									valueName={"TeamsSyncClientId"}
								/>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<FormTextField
									label={"Azure AD Client Secret"}
									placeholder="Client Secret"
									styleObject={{ width: "100%" }}
									formEdited={formEdited}
									setEdited={setFormEdited}
									toolTipMessage={CONFIG_TOOL_TIPS.TEAMS_SYNC_CLIENT_SECRET}
									startingValue={config?.TeamsSyncConfiguration?.TeamsSyncClientSecret || ""}
									setConfigValue={setNewConfigValue}
									maskValue
									valueName={"TeamsSyncClientSecret"}
									helperText={getClientSecretWarning()}
									errorCheck={getClientSecretExpiryError}
								/>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<KeyboardDatePicker
									autoComplete="new-password" // prevents showing auto fill dropdown in Chrome
									disableToolbar
									variant="inline"
									format={"MM/dd/yyyy"}
									margin="normal"
									id="client-secret-expires-date-picker-inline"
									label="Azure AD Client Secret Expiration Date (Optional)"
									value={config?.TeamsSyncConfiguration?.TeamsSyncClientSecretExpiryDate || null}
									disablePast
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<Tooltip
													arrow
													interactive
													placement={"bottom-start"}
													title={CONFIG_TOOL_TIPS.TEAMS_SYNC_CLIENT_SECRET_EXPIRY_DATE}
												>
													<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
												</Tooltip>
											</InputAdornment>
										),
									}}
									minDateMessage="" // empty required for hiding past dates error message.
									onChange={(newStartDate) => {
										// handles when invalid date entered manually, disables the user to save. eg: 17/15/2022
										// empty date (null) or valid date.
										setFormEdited(!(newStartDate !== null && isNaN(newStartDate.getDate())));
										setNewConfigValue("TeamsSyncClientSecretExpiryDate", newStartDate);
									}}
									KeyboardButtonProps={{
										"aria-label": "change date",
									}}
									style={{ margin: 0, width: "100%" }}
								/>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<FormTextField
									label={"Azure AD Username"}
									placeholder="Username"
									formEdited={formEdited}
									setEdited={setFormEdited}
									styleObject={{ width: "100%" }}
									toolTipMessage={CONFIG_TOOL_TIPS.TEAMS_SYNC_USERNAME}
									startingValue={config?.TeamsSyncConfiguration?.TeamsSyncUsername || ""}
									setConfigValue={setNewConfigValue}
									valueName={"TeamsSyncUsername"}
								/>
							</Grid>
							<Grid item xs={12} lg={6} className="mt-2">
								<FormTextField
									label={"Azure AD User Password"}
									placeholder="Password"
									formEdited={formEdited}
									styleObject={{ width: "100%" }}
									setEdited={setFormEdited}
									toolTipMessage={CONFIG_TOOL_TIPS.TEAMS_SYNC_USER_PASSWORD}
									startingValue={config?.TeamsSyncConfiguration?.TeamsSyncUserPassword || ""}
									setConfigValue={setNewConfigValue}
									maskValue
									valueName={"TeamsSyncUserPassword"}
								/>
							</Grid>

							<Dialog
								open={documentationOpen}
								onClose={() => {
									setDocumentationOpen(false);
								}}
								fullWidth
								maxWidth={"lg"}
							>
								<DialogTitle id="confirm-dialog-title">
									<div style={{ justifyContent: "space-between", display: "flex" }}>
										<div>Documentation</div>
										<div>
											<IconButton size="small" style={{ margin: "0px" }} onClick={() => setDocumentationOpen(false)}>
												<CloseIcon />
											</IconButton>
										</div>
									</div>
								</DialogTitle>
								<TeamsSyncSetupDocumentation />
							</Dialog>
						</>
					)}
				</Grid>
			</Card>
		</CardContent>
	);
};
